import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'

/**
 * Page title, optional record count, and the page's primary action, on one row.
 *
 * Every tab previously composed this by hand, so the title sat on its own line
 * and each page's primary button landed somewhere different — floating right,
 * stacked below, or inline with the search field. Same job, same layout,
 * everywhere (DESIGN.md §02 "One Pattern, Reused Everywhere").
 */
const styles = () => ({
  root: {
    // Breathing room from the fixed top navigation. Lives here (on content that
    // scrolls away) rather than as padding-top on the scroll container, which
    // would leave a gap above the sticky table header while scrolling.
    // 30px, not a spacing token: titles must line up with Headline's top margin.
    marginTop: 30,
  },
  // Headline's 57px row height; title, count and action centred in it.
  header: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 57,
    gap: 'var(--careco-space-md)',
    flexWrap: 'wrap',
    marginBottom: 'var(--careco-space-xl)',
  },
  // Title and record count on one line, count centred against the title.
  titleGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 'var(--careco-space-sm)',
    flexWrap: 'wrap',
    marginRight: 'auto',
    minWidth: 0,
  },
  // §05 {typography.page-title} — 36px / 700 / 1.2.
  title: {
    margin: 0,
    fontFamily: 'var(--careco-font)',
    fontSize: 36,
    fontWeight: 700,
    lineHeight: 1.2,
    color: 'var(--careco-text-primary)',
    wordBreak: 'break-word',
  },
  // A count pill rather than a faint number: tinted with the brand colour
  // (blue on CareBrain, green on QCS) so "how many rows" is legible at a glance.
  count: {
    display: 'inline-flex',
    alignItems: 'center',
    marginTop: 3,
    padding: '4px 13px',
    borderRadius: 'var(--careco-rounded-pill)',
    background: 'var(--careco-primary-light)',
    color: 'var(--careco-primary)',
    fontFamily: 'var(--careco-font)',
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '0.01em',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  description: {
    margin: '4px 0 0',
    fontFamily: 'var(--careco-font)',
    fontSize: 14,
    color: 'var(--careco-text-secondary)',
    maxWidth: '78ch',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--careco-space-sm)',
  },
})

const PageHeader = ({ classes, title, count, description, actions }) => (
  <div className={classes.root}>
    <div className={classes.header}>
      <div className={classes.titleGroup}>
        <h1 className={classes.title}>{title}</h1>
        {count != null && (
          <span className={classes.count}>
            {typeof count === 'number' ? count.toLocaleString() : count}
          </span>
        )}
      </div>
      {actions && <div className={classes.actions}>{actions}</div>}
    </div>
    {description && <p className={classes.description}>{description}</p>}
  </div>
)

PageHeader.propTypes = {
  classes: PropTypes.object.isRequired,
  title: PropTypes.node.isRequired,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  description: PropTypes.node,
  actions: PropTypes.node,
}

export default withStyles(styles)(PageHeader)
